<template>
  <div class="cover">
    <div @click="openModal">
      <slot></slot>
    </div>

    <Modal :is-modal-active="isModalVisible || active" @close="closeModal">
      <template #title> Profile settings </template>

      <CustomInput
        v-model="passwordNew"
        class-name="custom-input"
        type="password"
        placeholder="Current password"
        input-type="password"
      />

      <CustomInput
        v-model="passwordOld"
        class-name="custom-input"
        type="password"
        placeholder="New password"
        input-type="password"
      />

      <RippleButton type="prime" @click="changePassword">
        Change password
      </RippleButton>

      <div v-if="user && user.subscriptions" class="subscription">
        <h3>{{ $t("Subscriptions") }}</h3>
        <ul>
          <li v-for="sub in user.subscriptions" :key="sub._id">
            {{ sub.status === "active" ? "✅" : "❌" }}
            <!-- B-17: имя плана приходит с бэка (снимок Subscription.name);
                 планы задаются в админке и не переводятся — выводим как есть -->
            {{ sub.name || sub.type }} - {{ endDate(sub.endedAt) }}
            <button
              v-if="
                Boolean(sub.stripeId) &&
                sub.status === 'active' &&
                sub.subStatus != 'canceled'
              "
              class="sub-cancel"
              @click="cancelSub(sub._id)"
            >
              {{ sub.subStatus === "trialing" ? "Free" : "" }} Cancel ❌
            </button>
            <button
              v-if="
                Boolean(sub.stripeId) &&
                sub.status === 'active' &&
                sub.subStatus === 'canceled'
              "
              class="sub-cancel"
              @click="continueSub(sub._id)"
            >
              Сontinue ✅
            </button>
          </li>
        </ul>
      </div>

      <div class="bots">
        <h3 class="bots-title">{{ $t("join_the_bot") }}</h3>
        <!-- A-11 пример: замок по праву LIVE_SIGNAL -->
        <Lock permission="LIVE_SIGNAL" @close="closeModal">
          <a class="tg_link" :href="liveLink" target="_blank">
            <TelegramIcon width="30" /> LIVE
          </a>
        </Lock>

        <!-- A-11 пример: замок по праву TICKER_SIGNAL -->
        <Lock permission="TICKER_SIGNAL" @close="closeModal">
          <a class="tg_link" :href="tickerLink" target="_blank">
            <TelegramIcon width="30" /> TICKER
          </a>
        </Lock>
        <!-- A-9 пример: замок по праву PREMATCH_SIGNAL -->
        <Lock permission="PREMATCH_SIGNAL" @close="closeModal">
          <a class="tg_link" :href="prematchLink" target="_blank">
            <TelegramIcon width="30" /> PREMATCH
          </a>
        </Lock>
        <!-- A-9: у RAISE/CRASH нет собственных permission-ключей (нет в ТЗ) —
             временно вешаем на TICKER_SIGNAL (см. пометку в PERMISSIONS-PLAN) -->
        <Lock permission="TICKER_SIGNAL" @close="closeModal">
          <a class="tg_link" :href="raiseLink" target="_blank">
            <TelegramIcon width="30" /> RAISE
          </a>
        </Lock>
        <Lock permission="TICKER_SIGNAL" @close="closeModal">
          <a class="tg_link" :href="crashLink" target="_blank">
            <TelegramIcon width="30" /> CRASH
          </a>
        </Lock>
        <p class="bots-disclaimer">{{ $t("bots_disclaimer") }}</p>
      </div>

      <div v-if="user" class="device">
        <h3 class="device-title">Devices</h3>
        <ul class="device-list">
          <li class="device-item device-item-title">
            <span>Name</span><span>Last activity</span>
          </li>
          <li
            v-for="(device, index) in user.devices"
            :key="index"
            class="device-item"
            :class="{
              'is-login': device.active,
              'is-current': device.id == user.uuid,
            }"
          >
            <span>
              {{ device.name }} {{ device.id == user.uuid ? "(Current)" : "" }}
            </span>
            <span>{{ device.lastActivity }}</span>
            <button
              v-if="device.id != user.uuid"
              class="delete"
              @click="deleteDevice(device.id)"
            ></button>
          </li>
        </ul>
      </div>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import moment from "moment";
import RippleButton from "../common/form/Button.vue";
import CustomInput from "../common/form/Input.vue";
import Modal from "./Modal.vue";
import TelegramIcon from "../common/icons/Telegram.vue";
import { useI18n } from "vue-i18n";
import Lock from "../common/Lock.vue";
const { t } = useI18n();
const { proxy } = getCurrentInstance();

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const passwordNew = ref("");
const passwordOld = ref("");
const isModalVisible = ref(false);
const isLoading = ref(false);

const user = computed(() => store.getters.gerUser);

const liveLink = computed(() =>
  user.value ? `https://t.me/dadbetlive_bot?start=${user.value._id}` : false,
);
const prematchLink = computed(() =>
  user.value
    ? `https://t.me/dadbet_prematch_bot?start=${user.value._id}`
    : false,
);
const tickerLink = computed(() =>
  user.value ? `https://t.me/dadbet_ticker_bot?start=${user.value._id}` : false,
);
const raiseLink = computed(() =>
  user.value ? `https://t.me/dadbet_raise_bot?start=${user.value._id}` : false,
);
const crashLink = computed(() =>
  user.value ? `https://t.me/dadbet_crash_bot?start=${user.value._id}` : false,
);

function deleteDevice(id) {
  if (isLoading.value) return;

  isLoading.value = true;
  store
    .dispatch("deleteDevice", id)
    .then(() => {
      notifySuccess("Device successfully deleted");
    })
    .catch((text) => {
      notifyError(text);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function continueSub(id) {
  if (isLoading.value) return;

  isLoading.value = true;
  store
    .dispatch("continueSub", id)
    .then(() => {
      notifySuccess("Subscription successfully renewed");
    })
    .catch((text) => {
      notifyError(text);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function cancelSub(id) {
  if (isLoading.value) return;

  isLoading.value = true;

  store
    .dispatch("cancelSub", id)
    .then(() => {
      notifySuccess("Subscription successfully canceled");
    })
    .catch((text) => {
      notifyError(text);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function notifySuccess(text) {
  proxy.$notify({
    type: "success",
    title: `✔️ ${t("Success")}`,
    text,
  });
}

function notifyError(text) {
  proxy.$notify({
    type: "error",
    title: `❌ ${t("Error")}`,
    text,
  });
}

function endDate(time) {
  return moment(time).format("D MMM YYYY");
}

function openModal() {
  isModalVisible.value = true;
}

function closeModal() {
  isModalVisible.value = false;
}

function changePassword() {
  if (isLoading.value) return;

  isLoading.value = true;

  store
    .dispatch("changePassword", {
      passwordNew: passwordNew.value,
      passwordOld: passwordOld.value,
    })
    .then((text) => {
      notifySuccess(text);
    })
    .catch((text) => {
      notifyError(text);
    })
    .finally(() => {
      isLoading.value = false;
    });
}
</script>
<style lang="scss" scoped>
.cover {
  overflow-y: auto;
  overflow-x: hidden;
}
.subscription {
  width: 100%;
}

.sub-cancel {
  background-color: var(--warning-background-opacity-medium);
  padding: 2px 10px;
  border-radius: 50px;
  border: solid 1px var(--back-color);
  margin-left: 10px;
}

.bots {
  display: flex;
  gap: 5px;
  justify-content: center;
  flex-wrap: wrap;

  .tg_link {
    display: flex;
    background-color: var(--warning-background-opacity-medium);
    padding: 2px;
    padding-right: 5px;
    border-radius: 50px;
    font-size: 13px;
    border: solid 1px var(--back-color);
    align-items: center;
    gap: 5px;
    cursor: pointer;
  }
}

.bots-title {
  font-weight: bold;
}

.bots-disclaimer {
  width: 100%;
  margin-top: 8px;
  color: var(--back-color);
  opacity: 0.75;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
}

.device {
  width: 100%;
  color: var(--back-color);
}

.device-title {
  text-align: center;
  margin-bottom: 10px;
}

.device-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.device-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--warning-background-opacity-low);
  padding-left: 8px;
  border-radius: 10px;
  overflow: hidden;
  min-height: 36px;

  span {
    padding-left: 8px;
  }
}

.is-login {
  background-color: var(--success-background-opacity-low);
}

.is-current {
  padding-right: 10px;
}

.device-item-title {
  width: 100%;
  background-color: unset;
  padding-right: 40px;
  min-height: unset;

  span {
    text-align: center;
    font-weight: 500;
    width: 45%;
  }
}
</style>
